import React, { useState, useEffect } from "react";
import { List, Avatar, Input, Button, Spin, Empty, Badge } from "antd";
import { SearchOutlined, UsergroupAddOutlined, UserAddOutlined } from "@ant-design/icons";
import { chatService } from "../../services/chatService";
import CreateGroupModal from "./CreateGroupModal";
import axios from "axios";

const API_BASE_URL = import.meta.env.VITE_APP_API;

const ChatList = ({ userId, onChatSelect }) => {
  const [loading, setLoading] = useState(true);
  const [chats, setChats] = useState([]);
  const [search, setSearch] = useState("");
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [showUserList, setShowUserList] = useState(false);

  useEffect(() => {
    if (!userId) return;
    const unsubscribe = chatService.subscribeToChats(userId, (data) => {
      setChats(data);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [userId]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/chat-notification/users`);
      setAllUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  const startPrivateChat = async (targetUser) => {
    try {
        const chat = await chatService.createChat(
          { id: userId, name: localStorage.getItem("user") },
          { id: targetUser.id, name: targetUser.name, type: targetUser.type }
        );
        onChatSelect(chat);
        setShowUserList(false);
    } catch (error) {
        console.error("Failed to start chat:", error);
    }
  };

  const filteredChats = chats.filter((c) => {
    const name = c.isGroup ? c.groupName : (
      c.participantsData[Object.keys(c.participantsData).find(id => id != userId)]?.name || ""
    );
    return (name || "").toLowerCase().includes(search.toLowerCase());
  });

  const participantList = allUsers.filter(u => u.id !== userId && (u.name || "").toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="flex flex-col h-full bg-gray-50 overflow-hidden">
      <div className="p-4 bg-white border-b sticky top-0 z-10 flex flex-col gap-3">
        <Input
          prefix={<SearchOutlined className="text-gray-400" />}
          placeholder={showUserList ? "Search users..." : "Search chats..."}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-full bg-gray-100 border-none px-4 py-2"
        />
        <div className="flex gap-2">
          <Button 
            className="flex-1 rounded-lg flex items-center justify-center gap-2 font-medium"
            onClick={() => {
              setShowUserList(!showUserList);
              if (!showUserList) fetchUsers();
            }}
            icon={showUserList ? <SearchOutlined /> : <UserAddOutlined />}
            type={showUserList ? "default" : "primary"}
            ghost={!showUserList}
          >
            {showUserList ? "View Chats" : "New Chat"}
          </Button>
          <Button 
            className="rounded-lg flex items-center justify-center px-4"
            icon={<UsergroupAddOutlined />}
            onClick={() => {
                fetchUsers();
                setIsGroupModalOpen(true);
            }}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto pb-4">
        {loading ? (
          <div className="flex justify-center items-center h-40"><Spin /></div>
        ) : showUserList ? (
          <List
            itemLayout="horizontal"
            dataSource={participantList}
            renderItem={(user) => (
              <List.Item 
                className="px-4 py-3 hover:bg-white cursor-pointer transition-colors border-b"
                onClick={() => startPrivateChat(user)}
              >
                <List.Item.Meta
                  avatar={<Avatar className="bg-[#3073f1] font-bold">{user.name?.[0]?.toUpperCase()}</Avatar>}
                  title={<span className="font-semibold text-gray-800">{user.name}</span>}
                  description={<span className="text-[10px] bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full uppercase tracking-wider font-bold">{user.type}</span>}
                />
              </List.Item>
            )}
            locale={{ emptyText: <Empty description="No users found" /> }}
          />
        ) : (
          <List
            itemLayout="horizontal"
            dataSource={filteredChats}
            renderItem={(chat) => {
              const otherId = Object.keys(chat.participantsData).find(id => id != userId);
              const chatData = chat.participantsData[otherId];
              const isUnread = chat.lastMessage && !chat.lastMessage.readBy.includes(userId);

              return (
                <List.Item 
                  className={`px-4 py-3 hover:bg-white cursor-pointer transition-colors border-b ${isUnread ? 'bg-blue-50' : ''}`}
                  onClick={() => onChatSelect(chat)}
                >
                  <List.Item.Meta
                    avatar={
                      <Badge dot={isUnread} color="#3073f1">
                        <Avatar 
                          className={chat.isGroup ? "bg-green-500 font-bold" : "bg-[#3073f1] font-bold"}
                          size={44}
                        >
                          {chat.isGroup ? <UsergroupAddOutlined /> : (chatData?.name?.[0]?.toUpperCase() || "?")}
                        </Avatar>
                      </Badge>
                    }
                    title={
                      <div className="flex justify-between items-center w-full">
                        <span className={`font-semibold ${isUnread ? 'text-black' : 'text-gray-800'}`}>
                          {chat.isGroup ? chat.groupName : chatData?.name}
                        </span>
                        {chat.lastMessage && (
                          <span className="text-[10px] text-gray-400">
                             {new Date(chat.lastMessage.timestamp?.seconds * 1000 || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                      </div>
                    }
                    description={
                      <div className="truncate text-sm text-gray-500">
                        {chat.lastMessage ? (
                          <>
                            <span className={isUnread ? "font-bold text-gray-700" : ""}>
                                {chat.lastMessage.senderId === userId ? "You: " : ""}
                                {chat.lastMessage.content}
                            </span>
                          </>
                        ) : "No messages yet"}
                      </div>
                    }
                  />
                </List.Item>
              );
            }}
            locale={{ emptyText: <Empty description="No conversations yet" /> }}
          />
        )}
      </div>

      <CreateGroupModal 
        open={isGroupModalOpen} 
        onClose={() => setIsGroupModalOpen(false)}
        users={allUsers}
        currentUserId={userId}
        onGroupCreated={(chat) => {
            onChatSelect(chat);
            setIsGroupModalOpen(false);
        }}
      />
    </div>
  );
};

export default ChatList;
